import React from 'react';
import i18n from '../../../utilities/i18n';
import DynamicTable, { ColumnConfig } from '../../table/DynamicTable';
import { useQuery } from '@tanstack/react-query';
import { deviceUploadHistoryQueryOptions } from '../../../queryOptions/device';
import DateCellRenderer from '../../table/DateCellRenderer';
import { Label, Table } from '@adc/parallax-component-library';
import { StudyDataRecord } from '../../../models';
import { StudyDataProcessStatus } from 'adc-sirius-module-common';
import { LabelType } from '@adc/parallax-component-library/types/components/Atoms/Label/common';
import { SystemNotes } from './SystemNotes';
import './UploadHistoryTable.scss';

type Props = {
  deviceId: string;
  showCard: boolean;
};

const getLabelType = (status: StudyDataProcessStatus): LabelType => {
  switch (status) {
    case StudyDataProcessStatus.Failed:
      return 'danger';
    case StudyDataProcessStatus.Pending:
      return 'caution';
    case StudyDataProcessStatus.DataExporterEnd:
      return 'success';
    default:
      return 'generic';
  }
};

export const UploadHistoryTable = ({ deviceId, showCard }: Props) => {
  const columnData: ColumnConfig[] = [
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'processStatus',
      getSortValue: (record: StudyDataRecord) =>
        StudyDataProcessStatus[record.processStatus] || '',
    },
    {
      label: i18n.t('Entity.systemNotes'),
      dataKey: 'systemNotes',
      isSortable: false,
    },
  ];

  const cellRenderer = (dataKey: string, studyDataRecord: StudyDataRecord) => {
    if (dataKey === 'createdOn') {
      return (
        <DateCellRenderer value={studyDataRecord[dataKey] || 0} key={dataKey} />
      );
    } else if (dataKey === 'systemNotes') {
      return (
        <Table.TextCell>
          <SystemNotes
            key={dataKey}
            systemNotes={studyDataRecord.systemNotes}
          />
        </Table.TextCell>
      );
    } else if (dataKey === 'processStatus') {
      return (
        <Table.LabelCell
          key={dataKey}
          type={getLabelType(studyDataRecord[dataKey])}
        >
          <Label.Text>
            {i18n.t(
              `UploadHistory.status.${StudyDataProcessStatus[studyDataRecord[dataKey]]}`,
            )}
          </Label.Text>
        </Table.LabelCell>
      );
    }
  };

  const deviceUploadRecords = useQuery(
    deviceUploadHistoryQueryOptions({
      key: deviceId,
    }),
  );

  return (
    <div className="upload-history-table">
      <DynamicTable
        columnData={columnData}
        cellRenderer={cellRenderer}
        rowData={deviceUploadRecords.data || []}
        isLoading={deviceUploadRecords.isPending}
        hasLoadingError={deviceUploadRecords.isError}
        title={showCard ? i18n.t('UploadHistory.title') : ''}
        description={i18n.t('UploadHistory.description')}
        showCard={showCard}
      />
    </div>
  );
};
